import React from 'react';
import dayjs from 'dayjs';
import { useTaskDrawer } from '../TaskDrawerContext';
import { formatMs } from '../utils/timeFormatters';
import { getFullName } from '../utils/personUtils';

import { getDownloadUrl, fetchTaskActivityLog } from '../../api/projectManagementApi';

function getActorName(person) {
    return getFullName(person) || 'System';
}

function getEntryTime(entry) {
    return new Date(entry.timestamp || 0).getTime();
}

/** Pick icon name + background colour for file type */
function getFileIconInfo(name) {
    const filename = (name || '').toLowerCase();
    if (filename.endsWith('.pdf'))
        return { icon: 'picture_as_pdf', bg: '#fde8e8', color: '#c0392b' };
    if (filename.endsWith('.png') || filename.endsWith('.jpg') || filename.endsWith('.jpeg') || filename.endsWith('.gif') || filename.endsWith('.svg') || filename.endsWith('.webp'))
        return { icon: 'image', bg: '#e8f4fd', color: '#2980b9' };
    if (filename.endsWith('.doc') || filename.endsWith('.docx') || filename.endsWith('.odt'))
        return { icon: 'description', bg: '#e8edf8', color: '#2b4da0' };
    if (filename.endsWith('.xls') || filename.endsWith('.xlsx') || filename.endsWith('.csv'))
        return { icon: 'table_chart', bg: '#e6f4ee', color: '#1a7a45' };
    if (filename.endsWith('.zip') || filename.endsWith('.rar') || filename.endsWith('.7z') || filename.endsWith('.tar') || filename.endsWith('.gz'))
        return { icon: 'folder_zip', bg: '#fef9e6', color: '#b7950b' };
    if (filename.endsWith('.mp4') || filename.endsWith('.mov') || filename.endsWith('.avi') || filename.endsWith('.mkv') || filename.endsWith('.webm'))
        return { icon: 'videocam', bg: '#f0e6fe', color: '#7d3c98' };
    if (filename.endsWith('.mp3') || filename.endsWith('.wav') || filename.endsWith('.m4a') || filename.endsWith('.ogg') || filename.endsWith('.flac'))
        return { icon: 'audio_file', bg: '#fde8f5', color: '#c0407a' };
    return { icon: 'attach_file', bg: '#efefef', color: '#666' };
}

/** Parses "📎 Attached: filename.jpg" style text comments into structured data */
function parseAttachmentText(text) {
    if (!text) return { cleanText: '', attachmentNames: [] };
    const attachmentNames = [];
    const cleanText = text.replace(/(.*?)\s*(?:📎|ðŸ“Ž)\s*Attached:\s*([^\n]+)/g, (match, prefix, attachmentsStr) => {
        attachmentsStr.split(',').forEach(n => attachmentNames.push(n.trim()));
        return prefix;
    }).trim();
    return { cleanText, attachmentNames };
}

function ActivityAttachmentChip({ name, attachment, taskId }) {
    const [actionLoading, setActionLoading] = React.useState(false);
    const { icon, bg, color } = getFileIconInfo(name);

    const handleAction = async (e) => {
        if (!attachment || !taskId) return;
        e.stopPropagation();
        if (attachment.publicUrl) {
            window.open(attachment.publicUrl, '_blank', 'noopener,noreferrer');
            return;
        }
        setActionLoading(true);
        try {
            const { data } = await getDownloadUrl(taskId, attachment._id, 'inline');
            if (data?.result?.downloadUrl) {
                window.open(data.result.downloadUrl, '_blank', 'noopener,noreferrer');
            }
        } catch {
            // ignore
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <div
            onClick={handleAction}
            className={`inline-flex items-center gap-2 p-1.5 bg-white border border-ed-surface-container-high rounded-lg text-xs text-ed-on-surface select-none max-w-[200px] transition-all ${attachment ? 'cursor-pointer hover:shadow-sm hover:border-primary/30 active:scale-[0.98]' : ''}`}
        >
            <div className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: bg }}>
                {actionLoading ? (
                    <span className="material-symbols-outlined text-xs animate-spin">sync</span>
                ) : (
                    <span className="material-symbols-outlined text-[12px]" style={{ color }}>{icon}</span>
                )}
            </div>
            <span className="truncate flex-1 font-semibold" title={name}>{name}</span>
        </div>
    );
}

/**
*/

function DescriptionDiffView({ previousValue, newValue }) {
    const [expanded, setExpanded] = React.useState(false);
    const cleanOld = (previousValue || '').replace(/<[^>]*>/g, '').trim();
    const cleanNew = (newValue || '').replace(/<[^>]*>/g, '').trim();

    if (!cleanOld && !cleanNew) return null;

    return (
        <div className="mt-1.5 text-xs">
            <button
                type="button"
                onClick={() => setExpanded((prev) => !prev)}
                className="inline-flex items-center gap-1 font-bold text-[#03488a] dark:text-[#00AAF1] hover:underline"
            >
                <span className="material-symbols-outlined text-xs">
                    {expanded ? 'expand_less' : 'visibility'}
                </span>
                <span>{expanded ? 'Hide Changes' : 'View Changes'}</span>
            </button>

            {expanded && (
                <div className="mt-2 space-y-1.5 p-2 rounded-lg bg-slate-50 dark:bg-neutral-900 border border-[#e7e8e9] dark:border-[#303030] text-xs">
                    {cleanOld && (
                        <div className="p-2 rounded bg-rose-50 dark:bg-rose-950/40 text-rose-800 dark:text-rose-300 border border-rose-200 dark:border-rose-900/40">
                            <span className="font-bold block text-[10px] uppercase tracking-wider text-rose-500 mb-0.5">Removed</span>
                            <p className="line-through whitespace-pre-wrap break-words">{cleanOld}</p>
                        </div>
                    )}
                    {cleanNew && (
                        <div className="p-2 rounded bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-900/40">
                            <span className="font-bold block text-[10px] uppercase tracking-wider text-emerald-500 mb-0.5">Added</span>
                            <p className="whitespace-pre-wrap break-words">{cleanNew}</p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/**
 * Maps an activityLog action string to a display label and tone colour.
 * Returns { verb, tone } — verb is the human-readable action phrase,
 * tone is a Tailwind bg class for the dot indicator.
 */
function getActivityMeta(action) {
    const map = {
        created: { verb: 'created this task', tone: 'bg-emerald-500' },
        title_changed: { verb: 'changed the title', tone: 'bg-violet-500' },
        description_changed: { verb: 'updated the description', tone: 'bg-violet-500' },
        status_changed: { verb: 'changed status', tone: 'bg-[#03488a]' },
        priority_changed: { verb: 'changed priority', tone: 'bg-orange-400' },
        assignee_changed: { verb: 'changed assignee', tone: 'bg-teal-500' },
        due_date_changed: { verb: 'changed due date', tone: 'bg-rose-400' },
        start_date_changed: { verb: 'changed start date', tone: 'bg-rose-300' },
        estimated_time_changed: { verb: 'updated estimated time', tone: 'bg-sky-400' },
        effort_corrected: { verb: 'corrected effort', tone: 'bg-sky-500' },
        collaborator_added: { verb: 'added a collaborator', tone: 'bg-indigo-400' },
        collaborator_removed: { verb: 'removed a collaborator', tone: 'bg-indigo-300' },
        reviewer_added: { verb: 'added a reviewer', tone: 'bg-purple-400' },
        reviewer_removed: { verb: 'removed a reviewer', tone: 'bg-purple-300' },
        project_added: { verb: 'linked a project', tone: 'bg-cyan-500' },
        project_removed: { verb: 'unlinked a project', tone: 'bg-cyan-400' },
        dependency_added: { verb: 'added a dependency', tone: 'bg-amber-400' },
        dependency_removed: { verb: 'removed a dependency', tone: 'bg-amber-300' },
        file_uploaded: { verb: 'uploaded a file', tone: 'bg-green-500' },
        file_removed: { verb: 'removed a file', tone: 'bg-red-400' },
        subtask_created: { verb: 'created a subtask', tone: 'bg-emerald-400' },
        subtask_completed: { verb: 'completed a subtask', tone: 'bg-emerald-600' },
        subtask_reopened: { verb: 'reopened a subtask', tone: 'bg-yellow-500' },
        subtask_deleted: { verb: 'deleted a subtask', tone: 'bg-red-400' },
        subtask_title_changed: { verb: 'renamed a subtask', tone: 'bg-violet-400' },
        ai_updated: { verb: 'improved description with AI', tone: 'bg-fuchsia-500' },
        commented: { verb: 'commented', tone: 'bg-slate-400' },
        progress_update: { verb: 'added a progress update', tone: 'bg-amber-500' },
    };
    return map[action] || { verb: action, tone: 'bg-slate-400' };
}

function buildActivityEntries(task, customActivityLogs) {
    if (!task) return [];

    const entries = [];

    // ── Task creation (from createdAt field) ──────────────────────────────────
    if (task.createdAt) {
        entries.push({
            id: `created-${task._id || task.createdAt}`,
            type: 'created',
            timestamp: task.createdAt,
            actor: getActorName(task.createdBy),
            title: 'created this task',
            detail: task.title,
            tone: 'bg-emerald-500',
        });
    }

    // ── Status history ────────────────────────────────────────────────────────
    (task.statusHistory || []).forEach((entry, index) => {
        const isCreation = !entry.from && entry.note === 'Task created';
        // Skip the seed "Task created" entry — the createdAt block above already covers it
        if (isCreation) return;
        entries.push({
            id: entry._id || `status-${index}`,
            type: 'status',
            timestamp: entry.changedAt,
            actor: getActorName(entry.changedBy),
            title: 'changed status',
            detail: entry.from ? `${entry.from} → ${entry.to}` : entry.to,
            note: entry.note && entry.note !== 'Task created' ? entry.note : '',
            tone: 'bg-[#03488a]',
        });
    });

    // ── Comments ──────────────────────────────────────────────────────────────
    (task.comments || []).forEach((comment) => {
        const isProgress = comment.source === 'progress_update' || comment.isSystemGenerated;
        entries.push({
            id: comment._id || `comment-${comment.createdAt}`,
            type: isProgress ? 'progress' : 'comment',
            timestamp: comment.createdAt,
            actor: getActorName(comment.author),
            title: isProgress ? 'added a progress update' : 'commented',
            detail: comment.text,
            tone: isProgress ? 'bg-amber-500' : 'bg-slate-400',
        });
    });

    // ── Activity log (field-level changes) ────────────────────────────────────
    (customActivityLogs !== undefined ? customActivityLogs : (task?.activityLog || [])).forEach((entry) => {
        // Skip 'created' action — already covered by the createdAt block above
        if (entry.action === 'created') return;

        const meta = getActivityMeta(entry.action);
        entries.push({
            id: entry._id || `activity-${entry.action}-${entry.performedAt}`,
            type: entry.action,
            timestamp: entry.performedAt,
            actor: getActorName(entry.performedBy),
            title: meta.verb,
            detail: entry.detail || '',
            previousValue: entry.previousValue,
            newValue: entry.newValue,
            tone: meta.tone,
        });
    });

    // ── Pinned total effort summary ───────────────────────────────────────────
    if (task.actualTimeMs > 0) {
        entries.push({
            id: 'effort-total',
            type: 'effort',
            timestamp: task.updatedAt || task.createdAt,
            actor: 'System',
            title: 'total effort logged',
            detail: formatMs(task.actualTimeMs),
            tone: 'bg-sky-500',
            pinned: true,
        });
    }

    // Pinned entries first, then newest → oldest
    return entries.sort((a, b) => {
        if (a.pinned && !b.pinned) return -1;
        if (!a.pinned && b.pinned) return 1;
        return getEntryTime(b) - getEntryTime(a);
    });
}

export default function TaskActivityLog() {
    const { task, mode } = useTaskDrawer();



    const taskId = task?._id;

    const [activityLogs, setActivityLogs] = React.useState(() => task?.activityLog || []);
    const [page, setPage] = React.useState(1);
    const [hasMore, setHasMore] = React.useState(false);
    const [totalCount, setTotalCount] = React.useState(0);
    const [loadingMore, setLoadingMore] = React.useState(false);
    const [hasFetchedInitial, setHasFetchedInitial] = React.useState(false);

    // Sync when task changes
    React.useEffect(() => {
        if (!taskId) return;
        setActivityLogs(task?.activityLog || []);
        setPage(1);
        setHasMore(false);
        setTotalCount((task?.activityLog || []).length);
        setHasFetchedInitial(false);
    }, [taskId]);

    const loadInitialPage = React.useCallback(async () => {
        if (!taskId || hasFetchedInitial) return;
        setHasFetchedInitial(true);
        try {
            const { data } = await fetchTaskActivityLog(taskId, { page: 1, limit: 15 });
            const items = data?.result || [];
            setActivityLogs(items);
            if (data?.pagination) {
                setHasMore(Boolean(data.pagination.hasMore));
                setTotalCount(data.pagination.total || items.length);
            }
        } catch {
            // graceful fallback
        }
    }, [taskId, hasFetchedInitial]);

    const handleLoadMore = async () => {
        if (!taskId || loadingMore || !hasMore) return;
        setLoadingMore(true);
        const nextPage = page + 1;
        try {
            const { data } = await fetchTaskActivityLog(taskId, { page: nextPage, limit: 15 });
            const items = data?.result || [];
            setActivityLogs((prev) => {
                const existingIds = new Set(prev.map((i) => String(i._id || i.performedAt)));
                const uniqueNew = items.filter((i) => !existingIds.has(String(i._id || i.performedAt)));
                return [...prev, ...uniqueNew];
            });
            setPage(nextPage);
            if (data?.pagination) {
                setHasMore(Boolean(data.pagination.hasMore));
                setTotalCount(data.pagination.total || 0);
            } else {
                setHasMore(false);
            }
        } catch {
            // non-critical
        } finally {
            setLoadingMore(false);
        }
    };

    const entries = buildActivityEntries(task, activityLogs);

    if (mode === 'create') return null;

    return (
        // Collapsed by default — no `open` attribute
        <details
            data-tour="task-activity-log"
            onToggle={(e) => {
                if (e.currentTarget.open) {
                    loadInitialPage();
                }
            }}
            className="group bg-[#f8f9fa] dark:bg-[#141414] border border-[#e7e8e9] dark:border-[#303030] rounded-xl overflow-hidden mt-6"
        >
            <summary className="flex justify-between items-center p-4 cursor-pointer list-none font-extrabold text-sm text-[#191c1d] dark:text-white">
                <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-[#03488a] dark:text-[#00AAF1] text-lg">history</span>
                    <span>Activity Log</span>
                    <span className="text-xs font-black text-[#03488a] dark:text-[#00AAF1] bg-[#dff2ff] dark:bg-blue-950/80 px-2 py-0.5 rounded-full">{totalCount || entries.length}</span>
                </div>
                <span className="material-symbols-outlined group-open:rotate-180 transition-transform">expand_more</span>
            </summary>
            <div className="p-4 pt-0 space-y-3 border-t border-[#e7e8e9] dark:border-[#303030] text-sm text-on-surface-variant max-h-[320px] overflow-y-auto">
                {entries.map((entry) => {
                    const isComment = entry.type === 'comment' || entry.type === 'progress';
                    const { cleanText, attachmentNames } = isComment
                        ? parseAttachmentText(entry.detail)
                        : { cleanText: entry.detail, attachmentNames: [] };

                    // For field-change entries, show prev → new inline when both values exist
                    const showDiff = !isComment
                        && entry.previousValue
                        && entry.newValue
                        && entry.type !== 'effort'
                        && entry.type !== 'created';

                    return (
                        <div key={entry.id} className="flex gap-3 rounded-lg bg-white dark:bg-[#1f1f1f] border border-[#e7e8e9] dark:border-[#303030] p-3">
                            <div className={`w-2 h-2 rounded-full ${entry.tone} mt-1.5 flex-shrink-0`} />
                            <div className="min-w-0 flex-1">
                                <div className="flex items-start justify-between gap-3">
                                    <p className="text-xs text-[#424750] dark:text-slate-300 leading-relaxed">
                                        <span className="font-black text-[#191c1d] dark:text-white">{entry.actor}</span>{' '}
                                        {entry.title}
                                    </p>
                                    {entry.timestamp && (
                                        <span className="text-xs text-[#737782] dark:text-slate-500 whitespace-nowrap">
                                            {dayjs(entry.timestamp).format('MMM D, h:mm A')}
                                        </span>
                                    )}
                                </div>

                                {/* Detail line */}
                                {cleanText && !showDiff && (
                                    <p className={`mt-1 ${entry.type === 'effort' ? 'text-sm font-black text-[#03488a] dark:text-[#7dd3fc]' : 'text-xs text-[#191c1d] dark:text-slate-200'} break-words leading-relaxed`}>
                                        {cleanText}
                                    </p>
                                )}

                                {/* Diff display: prev → new */}
                                {entry.type === 'description_changed' && (
                                    <DescriptionDiffView
                                        previousValue={entry.previousValue}
                                        newValue={entry.newValue}
                                    />
                                )}

                                {showDiff && entry.type !== 'description_changed' && (
                                    <p className="mt-1 text-xs text-[#424750] dark:text-slate-300 leading-relaxed flex items-center gap-1 flex-wrap">
                                        <span className="line-through text-[#737782] dark:text-slate-500">{entry.previousValue}</span>
                                        <span className="material-symbols-outlined text-[11px] text-[#03488a]">arrow_forward</span>
                                        <span className="font-semibold text-[#191c1d] dark:text-white">{entry.newValue}</span>
                                    </p>
                                )}

                                {/* Comment attachment chips */}
                                {attachmentNames.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5 mt-2">
                                        {attachmentNames.map((name, idx) => {
                                            const matched = (task?.attachments || []).find(att => att.originalName === name);
                                            return (
                                                <ActivityAttachmentChip
                                                    key={idx}
                                                    name={name}
                                                    attachment={matched}
                                                    taskId={task?._id}
                                                />
                                            );
                                        })}
                                    </div>
                                )}

                                {entry.note && <p className="mt-1 text-xs italic text-[#737782] dark:text-slate-400">{entry.note}</p>}
                            </div>
                        </div>
                    );
                })}
                {entries.length === 0 && (
                    <p className="text-xs text-on-surface-variant opacity-60 text-center py-4">No activity logged yet.</p>
                )}

                {hasMore && (
                    <div className="pt-2 pb-1 text-center">
                        <button
                            type="button"
                            disabled={loadingMore}
                            onClick={handleLoadMore}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-[#03488a] dark:text-[#00AAF1] bg-white dark:bg-[#1f1f1f] border border-[#e7e8e9] dark:border-[#303030] rounded-lg hover:bg-slate-50 dark:hover:bg-neutral-800 transition-colors shadow-sm disabled:opacity-50 cursor-pointer"
                        >
                            {loadingMore ? (
                                <>
                                    <span className="material-symbols-outlined text-xs animate-spin">sync</span>
                                    <span>Loading older activities...</span>
                                </>
                            ) : (
                                <>
                                    <span className="material-symbols-outlined text-xs">expand_more</span>
                                    <span>Load More Activities ({entries.length} of {totalCount || entries.length})</span>
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>
        </details>
    );
}
